import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";
import { useBlogContent } from "../hooks/useBlogContent";

function BlogDetailPage() {
  const { slug } = useParams();
  const { getPostBySlug } = useBlogContent();
  const blogPost = getPostBySlug(slug);

  if (!blogPost) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Link 
          to="/blog" 
          className="back-to-blog inline-flex items-center text-secondary mb-6 font-medium transition-all duration-200"
        >
          ← Blog'a Dön
        </Link>
        <div className="text-center py-16">
          <h1 className="text-3xl font-bold text-secondary mb-4">
            Blog Yazısı Bulunamadı
          </h1>
          <p className="text-gray-600 mb-8">
            Aradığınız blog yazısı mevcut değil veya kaldırılmış olabilir.
          </p>
          <Link
            to="/blog"
            className="inline-block bg-secondary text-label py-2 px-6 rounded-md hover:bg-primary hover:text-secondary transition-colors duration-300"
          >
            Tüm Yazıları Görüntüle
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    const renderInline = (text) => {
      const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
      return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={index} className="font-semibold text-secondary">
              {part.slice(2, -2)}
            </strong>
          );
        }

        if (part.startsWith('*') && part.endsWith('*')) {
          return (
            <em key={index} className="italic">
              {part.slice(1, -1)}
            </em>
          );
        }

        return part;
      });
    };

    const paragraphs = content.split('\n\n');
    const result = [];
    let i = 0;

    while (i < paragraphs.length) {
      const paragraph = paragraphs[i];

      // Main title (with ###)
      if (paragraph.startsWith('###')) {
        result.push(
          <h3 key={i} className="text-xl md:text-2xl font-bold text-secondary mt-8 mb-4 leading-snug break-words">
            {paragraph.replace('###', '').trim()}
          </h3>
        );
        i++;
        continue;
      }
      
      // Subheading (with ##)
      if (paragraph.startsWith('##')) {
        result.push(
          <h2 key={i} className="text-3xl font-bold text-secondary mt-10 mb-6">
            {paragraph.replace('##', '').trim()}
          </h2>
        );
        i++;
        continue;
      }

      // Check for unordered list
      if (paragraph.match(/^[*-]\s/)) {
        const bulletItems = [];
        let j = i;

        while (j < paragraphs.length && paragraphs[j].match(/^[*-]\s/)) {
          bulletItems.push(paragraphs[j]);
          j++;
        }

        result.push(
          <ul key={i} className="list-disc list-inside space-y-3 my-6 text-gray-700">
            {bulletItems.map((item, itemIndex) => {
              const withoutBullet = item.replace(/^[*-]\s/, '').trim();
              const [heading, ...rest] = withoutBullet.split(':');
              const hasRest = rest.length > 0;

              return (
                <li key={itemIndex} className="leading-relaxed">
                  {hasRest ? (
                    <>
                      <span className="font-semibold text-secondary">{heading.trim()}</span>
                      {': '}
                      {renderInline(rest.join(':').trim())}
                    </>
                  ) : (
                    renderInline(withoutBullet)
                  )}
                </li>
              );
            })}
          </ul>
        );

        i = j;
        continue;
      }

      // Check for numbered list
      if (paragraph.match(/^\d\./)) {
        // Collect all consecutive numbered items
        const numberedItems = [];
        let j = i;
        
        while (j < paragraphs.length && paragraphs[j].match(/^\d\./)) {
          const itemText = paragraphs[j];
          const nextParagraph = paragraphs[j + 1];
          const hasDetail = nextParagraph && !nextParagraph.match(/^(##|###|\d\.|[-*]\s)/);

          numberedItems.push({
            text: itemText,
            detail: hasDetail ? nextParagraph : null
          });

          j += hasDetail ? 2 : 1;
        }

        result.push(
          <ol key={i} className="list-decimal list-inside space-y-4 my-6 text-lg text-gray-700">
            {numberedItems.map((item, itemIndex) => {
              const withoutNumber = item.text.replace(/^\d\./, '').trim();
              const [heading, ...rest] = withoutNumber.split(':');
              return (
                <li key={itemIndex} className="leading-relaxed">
                  <span className="font-semibold text-secondary inline">
                    {heading.replace(/\*/g, '').trim()}
                  </span>
                  {rest.length > 0 && (
                    <div className="mt-2 ml-5 text-gray-700">
                      {renderInline(rest.join(':').trim())}
                    </div>
                  )}
                  {item.detail && (
                    <div className="mt-2 ml-5 text-gray-700">
                      {renderInline(item.detail)}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        );
        
        i = j; // Skip processed items
        continue;
      }

      // Regular paragraphs
      result.push(
        <p key={i} className="text-lg text-gray-700 leading-relaxed mb-6">
          {renderInline(paragraph)}
        </p>
      );
      i++;
    }

    return result;
  };

  return (
    <>
      <Helmet>
        <title>{blogPost.seo?.title || `${blogPost.title} | Yağmur Demiröz`}</title>
        <meta name="description" content={blogPost.seo?.description || blogPost.excerpt} />
        <meta name="keywords" content={blogPost.seo?.keywords || ""} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content={blogPost.author} />
        <link rel="canonical" href={`https://yagmurdemiroz.com/blog/${blogPost.slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://yagmurdemiroz.com/blog/${blogPost.slug}`} />
        <meta property="og:title" content={blogPost.seo?.title || blogPost.title} />
        <meta property="og:description" content={blogPost.seo?.description || blogPost.excerpt} />
        <meta property="og:image" content={blogPost.image.startsWith('http') ? blogPost.image : `https://yagmurdemiroz.com${blogPost.image}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="tr_TR" />
        <meta property="og:site_name" content="Yağmur Demiröz - Psikolog" />
        <meta property="article:published_time" content={blogPost.date} />
        <meta property="article:author" content={blogPost.author} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://yagmurdemiroz.com/blog/${blogPost.slug}`} />
        <meta name="twitter:title" content={blogPost.seo?.title || blogPost.title} />
        <meta name="twitter:description" content={blogPost.seo?.description || blogPost.excerpt} />
        <meta name="twitter:image" content={blogPost.image.startsWith('http') ? blogPost.image : `https://yagmurdemiroz.com${blogPost.image}`} />
        <meta name="twitter:image:alt" content={blogPost.title} />
        
        {/* WhatsApp (uses Open Graph) */}
        <meta property="og:image:alt" content={blogPost.title} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blogPost.title,
            "datePublished": blogPost.date,
            "author": {
              "@type": "Person",
              "name": blogPost.author
            },
            "articleBody": blogPost.content,
            "url": `https://yagmurdemiroz.com/blog/${blogPost.slug}`,
            "image": blogPost.image.startsWith('http') ? blogPost.image : `https://yagmurdemiroz.com${blogPost.image}`
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Ana Sayfa",
                "item": "https://yagmurdemiroz.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://yagmurdemiroz.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": blogPost.title,
                "item": `https://yagmurdemiroz.com/blog/${blogPost.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <Link 
          to="/blog" 
          className="back-to-blog inline-flex items-center text-secondary mb-6 font-medium transition-all duration-200"
        >
          ← Blog'a Dön
        </Link>

        <div className="flex flex-col md:flex-row items-start">
          <div className="w-full md:w-2/3 mb-6 md:mb-0">
            <article>
              <h1 className="text-4xl font-bold text-secondary mb-6">
                {blogPost.title}
              </h1>
              
              <div className="flex items-center text-secondary/80 mb-8">
                <span className="mr-4 font-medium">{blogPost.date}</span>
                <span className="font-medium">Yazar: {blogPost.author}</span>
              </div>

              <div className="prose max-w-none space-y-4">
                {renderContent(blogPost.content)}
              </div>
            </article>
          </div>

          <div className="w-full md:w-1/3 md:ml-8">
            <div className="sticky top-24">
              <img 
                src={blogPost.image} 
                alt={blogPost.title} 
                className="w-full rounded-lg shadow-lg mb-6"
                loading="lazy"
              />
              {/* İleride buraya ilgili yazılar, etiketler vs. eklenebilir */}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="bg-white border border-t-primary py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="bg-label rounded-lg shadow-xl p-8 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-secondary mb-4">
                {blogPost.cta?.title || "Profesyonel Oyun Terapisi Desteği"}
              </h3>
              <p className="text-lg text-gray-700">
                {blogPost.cta?.description || "Çocuğunuzun gelişimi ve duygusal ihtiyaçları hakkında daha detaylı bilgi almak veya randevu oluşturmak için benimle iletişime geçebilirsiniz."}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center mb-2 text-secondary">
                  <FaPhoneAlt className="mr-2 text-xl" />
                  <strong>Telefon</strong>
                </div>
                <a href="tel:+905441354754" className="text-gray-800 hover:underline block">
                  +90 544 135 47 54
                </a>
              </div>

              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center mb-2 text-secondary">
                  <FaEnvelope className="mr-2 text-xl" />
                  <strong>Email</strong>
                </div>
                <a href="mailto:pskyagmurdemiroz@gmail.com" className="text-gray-800 hover:underline block">
                  pskyagmurdemiroz@gmail.com
                </a>
              </div>

              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center mb-2 text-secondary">
                  <FaMapMarkerAlt className="mr-2 text-xl" />
                  <strong>Adres</strong>
                </div>
                <a href="https://maps.app.goo.gl/sL9XxCRfdvr5y17P7" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-gray-800 hover:underline block">
                  Nispetiye / Etiler
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogDetailPage;
